import "katex/dist/katex.min.css";

import type { FC } from "react";
import { useState } from "react";
import Latex from "react-latex-next";

import SelfEvaluationSolution from "./SelfEvaluationSolution";
import Task from "./Task";

import type { UserAssignmentDetailFragment } from "api/generated";

import { useAppDispatch } from "store/hooks";
import type { TAssignmentStatus, TSolutionVariant } from "store/slices/exercise";
import { setIsTimerShown } from "store/slices/timer";

import { getEvaluationVariantFromOption } from "utils/getSolutionVariant";
import { useAssetUrlResolver } from "utils/hooks/useAssetUrlResolver";

export interface ISaveSelfEvaluationAnswerParams {
  userAssignmentId: string;
  selfEvalAnswer: Array<{ points: number | null; status: TSolutionVariant | null }>;
  status: TAssignmentStatus;
  answerVariants: TSolutionVariant[];
}

interface Props {
  userAssignment: UserAssignmentDetailFragment;
  userAssignmentId: string;
  videoTips?: Array<{ videoId: string; placeholderImage?: string | null | undefined }>;
  onNextClick?: () => void;
}

export const TutoringSelfEvaluation: FC<Props> = ({
  userAssignment,
  userAssignmentId,
  videoTips = [],
  onNextClick,
}) => {
  if (userAssignment.assignment === undefined || userAssignment.assignment === null) return null;

  const isShared = userAssignment.assignment?.selfEvaluation?.hasSharedSelfEvaluation === true;
  const questionsCount = Math.max(userAssignment.assignment?.assignment?.questions.length ?? 0, 1);
  const selfEvaluation = userAssignment.assignment?.selfEvaluation;

  const [shownStep, setShownStep] = useState<"task" | "options" | "summary" | "goNext">("task");
  const [selectedOptions, setSelectedOptions] = useState<
    Array<{ points: number | null; status: TSolutionVariant | null }>
  >(new Array(isShared ? 1 : questionsCount).fill({ points: null, status: null }));
  const [attempt, setAttempt] = useState(0);

  const solutionImageURL = useAssetUrlResolver(selfEvaluation?.sharedSelfEvaluation?.general?.image);
  const dispatch = useAppDispatch();

  const handleEvaluationSelect = (index: number, option: number) => {
    let maxPoints = 0;

    if (isShared) {
      const points = userAssignment.assignment?.selfEvaluation?.sharedSelfEvaluation?.options
        .map(option => option.scoreReal)
        .filter((point): point is number => typeof point === "number");
      maxPoints = points === undefined ? 0 : Math.max(...points);
    } else {
      const points = userAssignment.assignment?.assignment?.questions[index].selfEvaluation?.options
        .map(option => option.scoreReal)
        .filter((point): point is number => typeof point === "number");
      maxPoints = points === undefined ? 0 : Math.max(...points);
    }

    setSelectedOptions(options => {
      const copy = [...options];
      copy[index] = {
        points: option,
        status: getEvaluationVariantFromOption(option, maxPoints),
      };

      return copy;
    });
  };

  const handleTaskSubmit = () => {
    dispatch(setIsTimerShown(false));
    setShownStep("options");
  };

  const handleOptionsSubmit = () => {
    setShownStep("summary");
    return false;
  };

  const handleNextClick = () => {
    onNextClick?.();
  };

  const resetSelection = () => {
    setAttempt(prev => prev + 1);
    setSelectedOptions(new Array(isShared ? 1 : questionsCount).fill({ points: null, status: null }));
    setShownStep("task");
  };

  if (shownStep === "task")
    return <Task userAssignment={userAssignment} onSubmit={handleTaskSubmit} videoTips={videoTips} attempt={attempt} />;

  return (
    <div className="exercise__body">
      {(!!selfEvaluation?.sharedSelfEvaluation?.general?.text || !!solutionImageURL) && (
        <div>
          <Latex>{selfEvaluation?.sharedSelfEvaluation?.general?.text ?? ""}</Latex>
          {!!solutionImageURL && <img src={solutionImageURL} alt="" />}
        </div>
      )}
      <div>
        <SelfEvaluationSolution
          sendDataToBe={false}
          selectedOptions={selectedOptions}
          userAssignment={userAssignment}
          userAssignmentId={userAssignmentId}
          shownStep={shownStep}
          handleEvaluationSelect={handleEvaluationSelect}
          handleOptionsSubmit={handleOptionsSubmit}
          handleNextClick={handleNextClick}
          onResetSelection={resetSelection}
        />
      </div>
    </div>
  );
};
